import { Checkout } from '@df/checkout/checkout.interfaces';
import { IBasketData } from '@df/session/api/basket/basket';
import { Customer } from '@df/session/customer/customer.interfaces';

declare global {
    interface Window {
        // must be `exponea` to be compatible with old data
        exponea: NBloomreach.IExponea;
    }
}

declare module '@df/core/df-core.config' {
    export interface IDfConfig {
        bloomreach: {
            token: string;
            track?: {
                google_analytics?: boolean;
            };
        };
    }
}

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Basket {
        interface BasketData<
            TItemId extends number | string = number,
            TProductId extends number | string = number,
            TQuoteId extends number | string = number
        > {
            coupon_value?: number;
            cart_hash?: string;
        }

        interface Totals {
            grand_total_global?: number;
        }
    }
}

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            prefix?: string;
            gender?: string;
            hash?: string;
            exponea?: string;
        }
    }
}

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface ISuccessData {
            status?: string;
            hash?: string;
        }

        interface IAddress {
            title?: string;
        }
    }
}

declare module '@df/catalog/product/product' {
    interface IProductDataV2 {
        category_root_path?: number[];
    }
}

declare module '@df/catalog/category/category' {
    interface ICategoryDataV2 {
        category_root_path?: number[];
    }
}

export namespace NBloomreach {
    export const ENDPOINT = {
        BULK: 'exponea/bulk'
    };

    export interface IExponea {
        identify(customerIds: ICustomerIdentify | string, customerProperties?: Record<string, unknown>): void;
        track(eventId: EEvent, eventProperties: IEventProperties): void;
        update(customerData: ICustomerUpdate): void;
        getRecommendation(options: IRecommendationsOptions): void;
        start(config?: unknown): void;
    }

    export enum EEvent {
        Authentication = 'authentication', // core
        NewsletterConsent = 'consent', // core - by BE
        Purchase = 'purchase', // core - by BE
        PurchaseItem = 'purchase_item', // core - by BE
        StockNotification = 'lead_watchdog_stock', // core - by BE
        CartUpdate = 'cart_update', // performance
        ViewCategory = 'view_category', // performance
        ViewProduct = 'view_item', // performance
        Wishlist = 'wishlist', // performance
        Checkout = 'checkout', // personalization
        Search = 'onsite_search' // ???
    }

    export interface IEvent {
        name: EEvent;
        properties: IEventProperties;
    }

    export interface IBackendEvent extends IEvent {
        email: string;
    }

    export interface ICustomerIdentify {
        registered: string;
        magento_customer_id?: string;
        watchdog_email?: string;
    }

    export type IEventProperties = Record<string, unknown>;

    export enum ECartUpdateActions {
        Add = 'add',
        Remove = 'remove',
        Quantity = 'quantity_update',
        PromoAdd = 'add_promo',
        PromoRemove = 'remove_promo'
    }

    export type TItemsCartUpdateAction = ECartUpdateActions.Add | ECartUpdateActions.Remove | ECartUpdateActions.Quantity;

    export interface IProductEventProperties extends IEventProperties {
        product_id: string;
        title: string;
        category_1?: string;
        category_2?: string;
        category_3?: string;
        categories_path: string;
        category_id: string;
        categories_ids?: string[];
    }

    export interface IProductAndVariantLists {
        product_list: (ICartProductListItem | undefined)[];
        product_ids: (string | undefined)[];
        variant_list?: (ICartProductListItem | undefined)[];
        variant_ids?: (string | undefined)[];
    }

    export interface IItemEventProperties extends IProductEventProperties {
        price: number;
        price_local_currency: number;
        discount_value: number;
        original_price?: number;
        original_price_local_currency?: number;
        local_currency: string;
    }

    export interface IViewProductEventProperties extends IItemEventProperties {
        stock_level: number;
    }

    export interface ICartEventProperties extends IEventProperties {
        product_list: (ICartProductListItem | undefined)[];
        product_ids: (string | undefined)[];
        total_quantity: number;
        total_price: number;
        total_price_local_currency: number;
    }

    export interface ICartProductListItem {
        product_id: string;
        quantity: number;
    }

    export interface ICartUpdateEventProperties extends ICartEventProperties {
        action: ECartUpdateActions;
        page_type?: string;
    }

    export interface IPromoCartUpdateEventProperties extends ICartUpdateEventProperties {
        promo_code?: string;
        promo_value?: number;
    }

    export interface IBasketEventProperties {
        total_price: number;
        total_price_without_tax: number;
        quote_id: string;
        cart_hash: string;
        product_list: (ICartProductListItem | undefined)[];
        product_ids: (string | undefined)[];
    }

    export interface IBasketItemCartUpdateEventProperties extends IPromoCartUpdateEventProperties, IItemEventProperties {}

    export interface ISearchEventProperties extends IEventProperties {
        search_term: string;
        number_of_search_results: number;
    }

    export enum ENewsletterConsentActions {
        Accept = 'accept',
        Reject = 'reject'
    }

    export interface INewsletterConsent extends IEventProperties {
        action: ENewsletterConsentActions;
        category: 'weekly_newsletters_from_web' | 'newsletter';
        timestamp: number;
        identification_type: 'email';
        identification: string;
        source: 'public_api' | string;
        placement?: 'public_api' | string;
        valid_until: 'unlimited';
        message: string;
        current_website: string;
        optin_date?: string | null;
        optout_date?: string | null;
    }

    export enum EWishlistActions {
        Add = 'add',
        Remove = 'remove'
    }

    export interface IWishlistChangeEventProperties extends IEventProperties {
        action: EWishlistActions;
        price: number;
        price_local_currency: number;
        discount_value: number;
        original_price: number;
        original_price_local_currency: number;
        local_currency: string;
        product_id: string;
        title: string;
        category_1: string;
    }

    export interface ICheckoutEventProperties extends ICartEventProperties {
        promo_code?: string;
        payment_type?: string;
        delivery_type?: string;
        delivery_cost?: number;
        delivery_country?: string;
        delivery_city?: string;
        tax_value?: number;
        local_currency?: string;
        total_price_without_tax: number;
    }

    export interface ICheckoutStepChangeEventProperties extends ICheckoutEventProperties {
        step_title: string;
        step_number?: number;
        promo_total_value: number;
    }

    export interface ICustomerUpdate {
        first_name?: string;
        last_name?: string;
        gender?: string;
        title?: string;
        city?: string;
        country?: string;
        phone?: string;
        email?: string;
        birth_date?: number;
        cookies_accepted?: 'yes' | 'no';
        watchdog_email?: string;
        customer_consent_preferences?: string;
    }

    export interface ICheckoutSubmitData {
        submissionData: Checkout.ISubmissionData;
        basketData: IBasketData;
        customerData: Customer.ICustomerData;
        checkoutBackendData: Checkout.IData;
        checkoutData: Checkout.IQuoteData;
    }

    export interface IPurchaseProperties extends IEventProperties {
        purchase_id?: string;
        purchase_status?: string;
        total_price: number;
        total_price_without_tax: number;
        total_price_local_currency: number;
    }

    export interface IPurchaseItemEventProperties extends IItemEventProperties, IPurchaseProperties {
        quantity: number;
        discount_percentage: number;
    }

    export interface IPurchaseEventProperties extends IPurchaseProperties, ICheckoutEventProperties {
        promo_value: number;
    }

    export type TPromoCartUpdateAction = ECartUpdateActions.PromoAdd | ECartUpdateActions.PromoRemove;

    export interface ICurrentPromo {
        code: string | undefined | null;
        value: number;
    }

    export interface ICustomerAuthenticationEventProperties extends IEventProperties {
        logged_in: boolean;
        user_id?: string;
    }

    export interface IViewCategoryEventProperties extends IEventProperties {
        category_id: string;
        category_name: string;
        category_listed_products?: string[];
        category_1: string;
        categories_path: string;
        categories_ids?: string[];
        local_currency: string;
    }

    export interface IGeneralData {
        local_currency?: string;
        language?: string;
        domain?: string;
    }

    export interface IProductData {
        product_id: string;
        variant_id?: string;
        category_id?: string;
        category_1?: string;
        category_2?: string;
        category_3?: string;
        department?: string;
        product_type_filter?: string;
        style?: string;
        brand?: string;
        categories_path: string;
        categories_ids?: string[];
        price: number;
        original_price?: number;
        markdown_percentage?: number;
        markdown_value?: number;
        product_group?: string;
        sub_group?: string;
        typenumber?: string | number;
        groupnumber?: string | number;
        page_type?: string;
        sale_category?: boolean;
    }

    export interface IRecommendationsOptions<I = object> {
        recommendationId: string;
        fillWithRandom?: boolean;
        callback: (items: IRecommendationsItem[] | null) => void;
        // 10 default
        size?: number;
        // If present, the recommendations are related not only to a customer, but to
        // products with IDs specified in this array. Item IDs from catalog used to train the
        // recommendation model need to be used. Input product IDs in a dictionary as
        // {product_id: weight}, where the value weight determines the preference strength
        // for the given product (bigger number = higher preference).
        // Example:
        // {"product_id_1": 1, "product_id_2": 2,}
        items?: I;
        catalogFilter?: IRecommendationsCatalogFilter[];
        catalogAttributesWhitelist?: string[];
        strategy?: RecommendationsStrategy;
        categoryNames?: string[];
    }

    export interface IRecommendationsItem {
        active?: boolean;
        category_1_url?: string;
        category_id?: string;
        category_level_1?: string;
        category_level_2?: string;
        category_level_3?: string;
        category_path?: string;
        color?: string;
        date_added: number;
        description?: string;
        discount_value?: number;
        engine_name?: string;
        image?: string;
        image_fullsize?: string;
        item_id?: string;
        original_price?: number;
        price?: number;
        product_id?: string;
        recommendation_id: string;
        recommendation_variant_id?: string;
        stock_level?: number;
        title?: string;
        url?: string;
    }

    export interface IRecommendationsCatalogFilter {
        constraint: IRecommendationsCatalogFilterConstraint;
        property: string;
    }

    export interface IRecommendationsCatalogFilterConstraint {
        operands: IRecommendationsCatalogFilterConstraintOperand[];
        operator: RecommendationsCatalogFilterOperator;
        type: string;
    }

    export interface IRecommendationsCatalogFilterConstraintOperand {
        type: string;
        value: string;
    }

    export type RecommendationsCatalogFilterOperator =
        | RecommendationsCatalogFilterCommonOperators
        | RecommendationsCatalogFilterStringsOperators
        | RecommendationsCatalogFilterNumbersOperators
        | RecommendationsCatalogFilterBooleanOperators
        | RecommendationsCatalogFilterDatesOperators;

    export enum RecommendationsCatalogFilterCommonOperators {
        IsSet = 'is set',
        IsNotSet = 'is not set',
        HasValue = 'has value',
        HasNoValue = 'has no value'
    }

    export enum RecommendationsCatalogFilterStringsOperators {
        Equals = 'equals',
        DoesNotEqual = 'does not equal',
        In = 'in',
        NotIn = 'not in',
        Contains = 'contains',
        DoesNotContain = 'does not contain',
        StartsWith = 'starts with',
        EndsWith = 'ends with',
        MatchesRegex = 'matches regex'
    }

    export enum RecommendationsCatalogFilterNumbersOperators {
        EqualTo = 'equal to',
        InBetween = 'in between',
        LessThan = 'less than',
        GreaterThan = 'greater than'
    }

    export enum RecommendationsCatalogFilterBooleanOperators {
        IsTrue = 'is true',
        IsFalse = 'is false'
    }

    export enum RecommendationsCatalogFilterDatesOperators {
        MoreThan = 'more than',
        LessThan = 'less than',
        MatchesRange = 'matches range',
        MatchesCurrentDay = 'matches current day',
        MatchesCurrentMonth = 'matches current month',
        MatchesCurrentYear = 'matches current year',
        SecondMatches = 'second matches',
        MinuteMatches = 'minute matches',
        HourMatches = 'hour matches',
        WeekdayMatches = 'weekday matches',
        DayMatches = 'day matches',
        MonthMatches = 'month matches',
        YearMatches = 'year matches'
    }

    export enum RecommendationsStrategy {
        Winner = 'winner',
        Mix = 'mix'
    }

    export interface IRecommendationsEngineOptions {
        options?: string | IRecommendationsOptions;
    }

    export const RECOMMENDATIONS_ENGINE_ID = 'exponeaRecommendations';

    export const RECOMMENDATIONS_OPTIONS = 'exponeaOptions';

    export interface IRecommendationsEngineOptions {
        options?: string | IRecommendationsOptions;
    }

    export interface IStockNotificationData {
        newsletter?: boolean;
        email: string;
    }

    export interface IStockNotificationEventProperties extends IEventProperties {
        variant_id: string;
        store_id?: string;
    }
}
